/**
 * Native JavaScript date utilities to replace date-fns functions
 * Centralized module to avoid code duplication across the codebase
 */

// Core date validation
export const isValidDate = (date) => {
  return date instanceof Date && !isNaN(date.getTime());
};

// Date comparison functions
export const isBefore = (date1, date2) => date1 < date2;
export const isAfter = (date1, date2) => date1 > date2;

// Parse MM/dd/yyyy format
export const parseMMDDYYYY = (input) => {
  if (!input || typeof input !== 'string') return null;

  const match = input.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;

  const [, month, day, year] = match;
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

  // Verify the date components match (handles invalid dates like 02/30/2023)
  if (
    date.getFullYear() !== parseInt(year) ||
    date.getMonth() !== parseInt(month) - 1 ||
    date.getDate() !== parseInt(day)
  ) {
    return null;
  }

  return date;
};

// Parse M/d/yyyy format (flexible month/day)
export const parseMDYYYY = (input) => {
  // This uses the same logic as parseMMDDYYYY since the regex handles both
  return parseMMDDYYYY(input);
};

// Parse yyyy-MM-dd format
export const parseYYYYMMDD = (input) => {
  if (!input || typeof input !== 'string') return null;

  const match = input.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (!match) return null;

  const [, year, month, day] = match;
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

  // Verify the date components match
  if (
    date.getFullYear() !== parseInt(year) ||
    date.getMonth() !== parseInt(month) - 1 ||
    date.getDate() !== parseInt(day)
  ) {
    return null;
  }

  return date;
};

// Flexible date parser that handles multiple formats
export const parseFlexibleDate = (input, format) => {
  if (format === 'yyyy-MM-dd') {
    return parseYYYYMMDD(input);
  } else {
    return parseMMDDYYYY(input);
  }
};

// Format date to MM/dd/yyyy
export const formatToMMDDYYYY = (date) => {
  if (!isValidDate(date)) return '';

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
};

// Format date to yyyy-MM-dd
export const formatToYYYYMMDD = (date) => {
  if (!isValidDate(date)) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

// Date parser for M/d/yyyy to MM/dd/yyyy conversion (used in parsers.js)
export const dateParser = (val) => {
  if (val === undefined || val === null) return;

  const parsed = parseMDYYYY(val);
  if (!parsed) return val;

  return formatToMMDDYYYY(parsed);
};

// Date validator for MM/dd/yyyy format (used in validators/date.js)
export const validateMMDDYYYY = (input) => {
  const dateComp = parseMMDDYYYY(input);
  return dateComp ? isValidDate(dateComp) : false;
};
